import { useEffect, useRef } from 'react';

/**
 * Hook réutilisable : appelle une fonction de sauvegarde à intervalle fixe, uniquement quand il y a des changements.
 * Ne réinitialise pas l'état « modifié » ; le composant doit le faire dans le chemin de succès de la sauvegarde.
 *
 * @param {Object} options
 * @param {() => Promise<void>} options.save - Fonction de sauvegarde asynchrone (sans argument). Appelée quand l'intervalle s'écoule et getHasChanges() est true.
 * @param {() => boolean} options.getHasChanges - Indique s'il existe des modifications non enregistrées.
 * @param {number} [options.intervalMs=300000] - Intervalle en ms (défaut 5 minutes).
 * @param {boolean} [options.enabled=true] - Si false, l'intervalle n'est pas démarré.
 * @param {() => void} [options.onActivated] - Appelé une seule fois lorsque la sauvegarde périodique est activée.
 */
export function usePeriodicSave({
  save,
  getHasChanges,
  intervalMs = 5 * 60 * 1000,
  enabled = true,
  onActivated,
}) {
  const isSavingRef = useRef(false);
  const saveRef = useRef(save);
  const getHasChangesRef = useRef(getHasChanges);
  const onActivatedRef = useRef(onActivated);
  const hasActivatedRef = useRef(false);
  saveRef.current = save;
  getHasChangesRef.current = getHasChanges;
  onActivatedRef.current = onActivated;

  useEffect(() => {
    if (!enabled || typeof saveRef.current !== 'function' || typeof getHasChangesRef.current !== 'function') {
      return;
    }

    const id = setInterval(async () => {
      if (isSavingRef.current) return;

      if (!hasActivatedRef.current && typeof onActivatedRef.current === 'function') {
        onActivatedRef.current();
        hasActivatedRef.current = true;
      }

      if (!getHasChangesRef.current()) return;

      isSavingRef.current = true;
      try {
        await saveRef.current();
      } catch (_) {
        // console.error('Error saving:', _);
      } finally {
        isSavingRef.current = false;
      }
    }, intervalMs);

    return () => clearInterval(id);
  }, [enabled, intervalMs]);
}
